import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDataApi } from '../context/DataApiContext'

// `page` is the table's pagination param, not a user-applied filter.
const NON_FILTER_PARAMS = new Set(['page'])

// Tab-scoped flag so Réinitialiser → reload → fresh auto-apply doesn't loop.
// Cleared automatically when the tab closes.
const SUPPRESS_KEY_PREFIX = 'quick-filters:suppress-default:'

export function urlHasMeaningfulSearch(search) {
  if (!search) return false
  const params = new URLSearchParams(search)
  for (const key of params.keys()) {
    if (!NON_FILTER_PARAMS.has(key)) return true
  }
  return false
}

// Single-shot read: returns true the first time after markSuppressAutoApply
// was called and clears the flag. This way clearAllFilters → reload skips
// auto-apply once, then resumes normal behaviour on subsequent reloads.
export function consumeSuppressAutoApply(pageType) {
  try {
    const set = sessionStorage.getItem(SUPPRESS_KEY_PREFIX + pageType) === '1'
    if (set) sessionStorage.removeItem(SUPPRESS_KEY_PREFIX + pageType)
    return set
  } catch (e) {
    return false
  }
}

function markSuppressAutoApply(pageType) {
  try {
    sessionStorage.setItem(SUPPRESS_KEY_PREFIX + pageType, '1')
  } catch (e) {
    // sessionStorage unavailable — best-effort, ignore
  }
}

export function useQuickFilters({ pageType, location }) {
  const { dataProvider } = useDataApi()
  const [data, setData] = useState({
    public: [],
    private: [],
    defaultId: null,
  })
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const result = await dataProvider.quickFilter.list(pageType)
      setData(result)
      return result
    } finally {
      setLoading(false)
    }
  }, [dataProvider, pageType])

  // Initial load. Auto-apply of the default lives in
  // QuickFilterAutoApplyGuard (route level) so it can redirect *before* the
  // page mounts — soft navigation, no full reload, no double data fetch.
  // Depends only on `pageType` so the effect runs once per page mount even
  // though `dataProvider` is recreated on every parent render.
  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const result = await dataProvider.quickFilter.list(pageType)
        if (cancelled) return
        setData(result)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
    // dataProvider is intentionally excluded — it's recreated on every parent
    // render and would cause this effect to thrash. Its inner methods all
    // delegate to a stable getHTTPClient(), so the captured reference is safe.
    // eslint-disable-next-line
  }, [pageType])

  const allFilters = useMemo(
    () => [...(data.public || []), ...(data.private || [])],
    [data.public, data.private]
  )

  const defaultFilter = useMemo(
    () =>
      data.defaultId
        ? allFilters.find((f) => f.id === data.defaultId) || null
        : null,
    [data.defaultId, allFilters]
  )

  const activeFilter = useMemo(() => {
    const currentSearch = (location.search || '').replace(/^\?/, '')
    if (!currentSearch) return null
    return allFilters.find((f) => f.urlQuery === currentSearch) || null
  }, [location.search, allFilters])

  const applyFilter = useCallback((filter) => {
    // Hard navigation. The new URL has params, so urlIsEmpty=false on the
    // post-reload mount and auto-apply naturally skips — no need to mark
    // suppress here (which would otherwise stick for the rest of the tab
    // session and prevent the default from re-applying on later visits).
    const search = filter && filter.urlQuery ? `?${filter.urlQuery}` : ''
    window.location.assign(window.location.pathname + search)
  }, [])

  const clearAllFilters = useCallback(() => {
    // User explicitly cleared — don't immediately reapply the default on
    // the post-reload mount.
    markSuppressAutoApply(pageType)
    window.location.assign(window.location.pathname)
  }, [pageType])

  const saveCurrent = useCallback(
    async ({ name, description, isPublic }) => {
      const urlQuery = (location.search || '').replace(/^\?/, '')
      const result = await dataProvider.quickFilter.create({
        name,
        description: description || null,
        pageType,
        isPublic: !!isPublic,
        urlQuery,
      })
      await refresh()
      return result
    },
    [dataProvider, pageType, location.search, refresh]
  )

  const updateFilter = useCallback(
    async (id, payload) => {
      const result = await dataProvider.quickFilter.update(id, payload)
      await refresh()
      return result
    },
    [dataProvider, refresh]
  )

  const deleteFilter = useCallback(
    async (id) => {
      await dataProvider.quickFilter.remove(id)
      await refresh()
    },
    [dataProvider, refresh]
  )

  const setAsDefault = useCallback(
    async (id) => {
      await dataProvider.quickFilter.setDefault(id)
      await refresh()
    },
    [dataProvider, refresh]
  )

  const clearDefault = useCallback(async () => {
    await dataProvider.quickFilter.clearDefault(pageType)
    await refresh()
  }, [dataProvider, pageType, refresh])

  return {
    publicFilters: data.public || [],
    privateFilters: data.private || [],
    defaultFilter,
    activeFilter,
    loading,
    refresh,
    applyFilter,
    clearAllFilters,
    saveCurrent,
    updateFilter,
    deleteFilter,
    setAsDefault,
    clearDefault,
  }
}
